import { useEffect, useRef, type RefObject } from "react";
import { glitterColors, glitterTierForGain, type Spark } from "./sparkModel.js";
import { drawGlitterFrame } from "./sparkDraw.js";
import { glitterSpeed, tickSparks } from "./sparkTick.js";

const STAGE_H = 72;
const FILL_MS = 600;

type MeterGlitterProps = {
  fillRef: RefObject<HTMLElement | null>;
  gain: number;
};

function progressColor(el: HTMLElement): string {
  const value = getComputedStyle(el).getPropertyValue("--progress").trim();
  return value.startsWith("#") ? value : "#2db89a";
}

function fitCanvas(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, width: number) {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const nextW = Math.floor(width * dpr);
  const nextH = Math.floor(STAGE_H * dpr);
  if (canvas.width === nextW && canvas.height === nextH) return;
  canvas.width = nextW;
  canvas.height = nextH;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}

export function MeterGlitter({ fillRef, gain }: MeterGlitterProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const fill = fillRef.current;
    const host = canvas?.parentElement;
    if (!canvas || !fill || !host) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const tier = glitterTierForGain(gain);
    const colors = glitterColors(progressColor(host), tier);
    const sparks: Spark[] = [];
    let last = performance.now();
    let prevTip = -1;
    let raf = 0;
    let moving = true;
    const stop = () => { moving = false; };
    const timer = window.setTimeout(stop, FILL_MS);
    fill.addEventListener("animationend", stop);

    const loop = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const hostBox = host.getBoundingClientRect();
      const fillBox = fill.getBoundingClientRect();
      const width = Math.max(1, hostBox.width);
      fitCanvas(canvas, ctx, width);
      const tipX = fillBox.right - hostBox.left;
      const speed = glitterSpeed(prevTip, tipX, width);
      prevTip = tipX;
      sparks.splice(0, sparks.length, ...tickSparks(sparks, dt, tipX, STAGE_H / 2, speed, moving, tier === "breakthrough", colors.particles));
      const pulse = 0.45 + 0.55 * Math.abs(Math.sin(now / 90));
      drawGlitterFrame(ctx, width, STAGE_H, sparks, tipX, STAGE_H / 2, moving, pulse, colors);
      if (moving || sparks.length > 0) raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      fill.removeEventListener("animationend", stop);
      window.clearTimeout(timer);
      cancelAnimationFrame(raf);
    };
  }, [fillRef, gain]);

  return <canvas className="meter-glitter" ref={canvasRef} aria-hidden="true" />;
}
